import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Star, Sparkles, Check, ArrowRight, Clock, 
  Package, Wrench, Crown, Zap, Eye, MapPin, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' }
];

const GetFeaturedPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, api: getApi, loading: authLoading } = useAuth();
  const api = getApi();
  
  const [pricing, setPricing] = useState(null);
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedTradieDuration, setSelectedTradieDuration] = useState(7);
  const [myPromotions, setMyPromotions] = useState({ active: [], expired: [] });
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  // Check for success/cancel from Stripe
  const sessionId = searchParams.get('session_id');
  const cancelled = searchParams.get('cancelled');

  useEffect(() => {
    if (!authLoading && user) {
      fetchData();
    }
  }, [authLoading, user]);

  useEffect(() => {
    if (sessionId) {
      verifyPayment(sessionId);
    }
    if (cancelled) {
      toast.error('Payment was cancelled');
    }
  }, [sessionId, cancelled]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pricingRes, promotionsRes] = await Promise.all([
        api.get('/promotions/pricing'),
        api.get('/my-promotions')
      ]);
      setPricing(pricingRes.data);
      setMyPromotions(promotionsRes.data);

      // Fetch user's listings if seller
      if (user?.is_seller) {
        const listingsRes = await api.get('/listings/my');
        setListings(listingsRes.data.listings || []);
      }
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (sessionId) => {
    try {
      const res = await api.get(`/promotions/verify/${sessionId}`);
      if (res.data.status === 'completed') {
        toast.success('🎉 Your promotion is now active!');
        fetchData();
        // Clear URL params
        navigate('/get-featured', { replace: true });
      }
    } catch (err) {
      console.error('Failed to verify payment', err);
      toast.error('Failed to verify payment');
    }
  };

  const handlePurchase = async (type, listingId = null, durationDays = 7) => {
    try {
      setPurchasing(true);
      const res = await api.post('/promotions/purchase', {
        promotion_type: type,
        listing_id: listingId,
        duration_days: durationDays
      });
      
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error('Failed to create checkout', err);
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(false);
    }
  };

  const isListingPromoted = (listingId) => {
    return myPromotions.active?.some(p => p.listing_id === listingId);
  };

  const isTradiePromoted = () => {
    return myPromotions.active?.some(p => p.promotion_type === 'tradie');
  };

  const getStateName = (code) => {
    const state = AUSTRALIAN_STATES.find(s => s.value === code);
    return state?.label || code;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-stone-900 mb-4">Please log in</h2>
          <Button onClick={() => navigate('/login')}>Log In</Button>
        </div>
      </div>
    );
  }

  const tradiePricing = pricing?.tradie_pricing || { 7: { price: 20, label: "1 Week" }, 14: { price: 35, label: "2 Weeks" }, 30: { price: 65, label: "1 Month" } };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-[#FAFAF9] py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            Boost Your Visibility
          </div>
          <h1 className="text-4xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Get Featured
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Stand out from the crowd! Get your listings or profile featured on the homepage carousel
            and reach more potential customers.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <div className="bg-white rounded-xl p-6 border border-stone-200 text-center">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Eye className="h-6 w-6 text-amber-600" />
            </div>
            <h3 className="font-semibold text-stone-900 mb-2">More Views</h3>
            <p className="text-sm text-stone-500">Featured items appear on the homepage for maximum exposure</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-stone-200 text-center">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Star className="h-6 w-6 text-amber-600" />
            </div>
            <h3 className="font-semibold text-stone-900 mb-2">Stand Out</h3>
            <p className="text-sm text-stone-500">Gold &quot;Featured&quot; badge makes your listing pop</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-stone-200 text-center">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Zap className="h-6 w-6 text-amber-600" />
            </div>
            <h3 className="font-semibold text-stone-900 mb-2">More Sales</h3>
            <p className="text-sm text-stone-500">Featured sellers see up to 5x more inquiries</p>
          </div>
        </div>

        {/* Active Promotions */}
        {myPromotions.active?.length > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6 mb-8">
            <h3 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
              <Check className="h-5 w-5" />
              Your Active Promotions
            </h3>
            <div className="space-y-3">
              {myPromotions.active.map(promo => (
                <div key={promo.id} className="bg-white rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                    <div>
                      <p className="font-medium text-stone-900">
                        {promo.promotion_type === 'listing' 
                          ? promo.listing_title || 'Featured Listing'
                          : 'Featured Tradie Profile'}
                      </p>
                      <p className="text-sm text-stone-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Ends {new Date(promo.ends_at).toLocaleDateString('en-AU')}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                    Active
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Seller Listing Promotion */}
          {user?.is_seller && (
            <div className="bg-white rounded-2xl border-2 border-teal-200 overflow-hidden">
              <div className="bg-gradient-to-r from-sky-500 to-teal-600 p-6 text-white">
                <Package className="h-8 w-8 mb-3" />
                <h3 className="text-xl font-bold mb-1">Feature a Listing</h3>
                <p className="text-sky-100 text-sm">Perfect for sellers</p>
              </div>
              <div className="p-6">
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold text-stone-900">${pricing?.seller_listing_price || 10}</span>
                  <span className="text-stone-500 ml-2">/ listing / week</span>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <Check className="h-4 w-4 text-green-500" />
                    Featured on homepage carousel
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <Check className="h-4 w-4 text-green-500" />
                    Gold &quot;Featured&quot; badge
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <Check className="h-4 w-4 text-green-500" />
                    7 days of premium placement
                  </li>
                </ul>

                {listings.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-stone-700">Select a listing to feature:</p>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {listings.map(listing => (
                        <button
                          key={listing.id}
                          onClick={() => setSelectedListing(listing.id === selectedListing ? null : listing.id)}
                          disabled={isListingPromoted(listing.id)}
                          className={`w-full p-3 rounded-lg border text-left transition-colors ${
                            isListingPromoted(listing.id)
                              ? 'bg-green-50 border-green-200 cursor-not-allowed'
                              : selectedListing === listing.id
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-stone-200 hover:border-teal-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {listing.images?.[0] && (
                              <img src={listing.images[0]} alt="" className="w-12 h-12 rounded object-cover" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-stone-900 truncate">{listing.title}</p>
                              <p className="text-sm text-stone-500">${listing.price}</p>
                            </div>
                            {isListingPromoted(listing.id) && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Featured</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                    <Button
                      onClick={() => handlePurchase('listing', selectedListing)}
                      disabled={!selectedListing || purchasing}
                      className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
                    >
                      {purchasing ? 'Processing...' : 'Feature This Listing'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center text-stone-500 py-4">
                    <p>You don&apos;t have any listings yet.</p>
                    <Button 
                      variant="outline" 
                      className="mt-3"
                      onClick={() => navigate('/create-listing')}
                    >
                      Create a Listing
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tradie Profile Promotion */}
          {user?.is_tradie && (
            <div className="bg-white rounded-2xl border-2 border-blue-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
                <Wrench className="h-8 w-8 mb-3" />
                <h3 className="text-xl font-bold mb-1">Feature Your Profile</h3>
                <p className="text-blue-100 text-sm">Perfect for tradies</p>
              </div>
              <div className="p-6">
                {/* Location-based benefit explanation */}
                <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-100">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-800 text-sm">Location-Targeted Visibility</h4>
                      <p className="text-xs text-blue-600 mt-1">
                        Your profile will be shown to buyers in <strong>{getStateName(user.trade_service_state) || 'your service area'}</strong>. 
                        Buyers from Melbourne won&apos;t see ads from Brisbane tradies - ensuring you reach the right customers!
                      </p>
                    </div>
                  </div>
                </div>

                {/* Duration Selection */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-stone-700 mb-3">Choose your promotion duration:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(tradiePricing).map(([days, info]) => (
                      <button
                        key={days}
                        onClick={() => setSelectedTradieDuration(parseInt(days))}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          selectedTradieDuration === parseInt(days)
                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                            : 'border-stone-200 hover:border-blue-300'
                        }`}
                      >
                        <p className="text-lg font-bold text-stone-900">${info.price}</p>
                        <p className="text-xs text-stone-500">{info.label}</p>
                        {parseInt(days) === 30 && (
                          <span className="inline-block mt-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            Best Value
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <Check className="h-4 w-4 text-green-500" />
                    Featured in your state&apos;s carousel
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <Check className="h-4 w-4 text-green-500" />
                    <Users className="h-3 w-3" />
                    Shown to local buyers only
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <Check className="h-4 w-4 text-green-500" />
                    Gold &quot;Featured&quot; badge on profile
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <Check className="h-4 w-4 text-green-500" />
                    Priority placement in search
                  </li>
                </ul>

                {isTradiePromoted() ? (
                  <div className="text-center py-4">
                    <span className="inline-flex items-center gap-2 text-green-700 font-medium">
                      <Check className="h-5 w-5" />
                      Your profile is already featured!
                    </span>
                  </div>
                ) : (
                  <Button
                    onClick={() => handlePurchase('tradie', null, selectedTradieDuration)}
                    disabled={purchasing}
                    className="w-full bg-blue-700 hover:bg-blue-800"
                  >
                    {purchasing ? 'Processing...' : `Feature My Profile - $${tradiePricing[selectedTradieDuration]?.price || 20}`}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Why Get Featured - Tradie specific */}
        {user?.is_tradie && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 mb-8">
            <h3 className="text-xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              🌟 Why Get Featured?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">Reach Local Customers</h4>
                <p className="text-sm text-stone-600">
                  Featured tradies are shown to buyers in their service area. A buyer in Sydney won&apos;t see a tradie 
                  from Perth - your ad reaches people who can actually hire you!
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">Stand Out From Competition</h4>
                <p className="text-sm text-stone-600">
                  With the gold &quot;Featured&quot; badge and carousel placement, your profile gets priority visibility 
                  over non-featured tradies. First impressions matter!
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">Flexible Duration Options</h4>
                <p className="text-sm text-stone-600">
                  Choose 1 week for a quick boost, 2 weeks for steady exposure, or 1 month for maximum impact. 
                  The 30-day option offers the best value per day!
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">Track Your Results</h4>
                <p className="text-sm text-stone-600">
                  See how your promotion is performing and track the increase in profile views and 
                  customer inquiries during your featured period.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Not a seller or tradie? */}
        {!user?.is_seller && !user?.is_tradie && (
          <div className="text-center py-12">
            <Crown className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">Become a Seller or Tradie</h3>
            <p className="text-stone-500 mb-4">
              Subscribe to start listing items or advertising your trade services.
            </p>
            <Button onClick={() => navigate('/subscription')}>
              View Subscription Options
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GetFeaturedPage;
